import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryCircles from "@/components/CategoryCircles";
import HeroCarousel from "@/components/HeroCarousel";
import TrustBadges from "@/components/TrustBadges";
import SchemeBanner from "@/components/SchemeBanner";
import Testimonials from "@/components/Testimonials";
import { Reveal } from "@/components/motion";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("All");
  const [productsState, setProductsState] = useState("loading"); // loading | ok | error

  const loadProducts = () => {
    setProductsState("loading");
    api.get("/products").then((r) => { setProducts(r.data); setProductsState("ok"); }).catch(() => setProductsState("error"));
  };

  useEffect(() => {
    loadProducts();
    api.get("/products/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const shown = tab === "All" ? products : products.filter((p) => p.category === tab);

  return (
    <div data-testid="home-page">
      <CategoryCircles />
      <HeroCarousel />

      {/* BESTSELLERS */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-14" data-testid="featured-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Our Best Sellers</h2>
          <Link to="/shop" className="flex items-center gap-1.5 text-sm font-bold text-emerald-800 hover:text-emerald-900 transition-colors" data-testid="featured-view-all">
            View All <ArrowRight size={14} />
          </Link>
        </Reveal>
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8">
          {["All", ...categories].map((c) => (
            <button
              key={c}
              data-testid={`bestseller-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setTab(c)}
              className={`px-5 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-300 active:scale-95 ${tab === c ? "bg-emerald-800 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-400"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {productsState === "loading" && (
            <p className="col-span-full font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400 py-20 text-center" data-testid="bestsellers-loading">Loading products…</p>
          )}
          {productsState === "error" && (
            <div className="col-span-full py-20 text-center" data-testid="bestsellers-error">
              <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Could not load products</p>
              <button onClick={loadProducts} data-testid="bestsellers-retry-btn" className="mt-4 bg-emerald-800 text-white px-6 py-3 rounded-xl font-bold text-xs hover:bg-emerald-900 transition-colors">Retry</button>
            </div>
          )}
          {productsState === "ok" && shown.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <TrustBadges />

      {/* RX COMPLIANCE SPLIT */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-8" data-testid="trust-section">
        <Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-8 hover:shadow-lg transition-shadow duration-300">
              <span className="inline-flex bg-emerald-100 text-emerald-800 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">Licensed Trade Only</span>
              <h3 className="text-xl font-bold text-slate-900 mt-4">Rx Supply, Fully Compliant</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">Prescription medicines are supplied strictly to verified Drug License holders. No prescription uploads, no paperwork chase — just compliant trade ordering.</p>
              <Link to="/shop?rx=true" className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-emerald-800 hover:text-emerald-900 transition-colors" data-testid="trust-shop-cta">
                Browse the Rx range <ArrowRight size={14} />
              </Link>
            </div>
            <div className="rounded-2xl bg-emerald-900 p-8 hover:shadow-lg transition-shadow duration-300">
              <span className="inline-flex bg-amber-400 text-emerald-950 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">How Ordering Works</span>
              <h3 className="text-xl font-bold text-white mt-4">Pay First, Verify Instantly</h3>
              <p className="text-sm text-emerald-100/90 mt-2 leading-relaxed">Complete payment for your wholesale order, then submit your Drug License & GST number on the spot — your order is released for dispatch immediately.</p>
              <Link to="/shop" className="inline-flex items-center gap-1.5 mt-5 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors" data-testid="b2b-shop-cta">
                Start a bulk order <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <SchemeBanner />
      <Testimonials />
    </div>
  );
}

